import React, { useState } from 'react';
import { Plus, Sparkles, X } from 'lucide-react';
import { CURRENCIES_LIST, ccyStripeOk } from '@/utils/currencyUtils';
import { MAX_CURRENCIES } from '@/utils/publishForm';
import '@/styles/publish-c.css';

/**
 * Briques des deux formulaires de publication (colis, trajet), direction C — planches C-Publier-colis et
 * C-Publier-trajet, docs/design/ecarts-publication.md. Elles affichent : les règles sont dans `utils/publishForm.js`.
 */

/** Barre des étapes : chaque étape s'ouvre d'un clic (l'étape en cours porte aria-current="step"). */
export function StepBar({ steps, current, onGo }) {
  const idx = steps.findIndex((s) => s.key === current);
  return (
    <ol className="pf-steps" aria-label="Étapes" data-testid="publish-steps">
      {steps.map((s, i) => (
        <li key={s.key}>
          <button
            type="button"
            className={`pf-step${i <= idx ? ' is-done' : ''}${i === idx ? ' is-on' : ''}`}
            aria-current={i === idx ? 'step' : undefined}
            onClick={() => onGo(s.key)}
            data-testid={`step-${s.key}`}
          >
            <span className="pf-step-bar" aria-hidden="true" />
            {s.key === 'recap' ? s.label : `${i + 1}. ${s.label}`}
          </button>
        </li>
      ))}
    </ol>
  );
}

/** Carte d'une étape : « Étape N sur 4 », titre, contenu. */
export function StepCard({ eyebrow, title, children, testid }) {
  return (
    <section className="pf-card" aria-label={title} data-testid={testid}>
      <p className="pf-eyebrow">{eyebrow}</p>
      <h2 className="pf-title">{title}</h2>
      {children}
    </section>
  );
}

/** Navigation d'une étape : « Précédent » à gauche, l'action principale à droite (et « Passer cette étape »). */
export function StepNav({ onBack, onNext, nextLabel = 'Suivant', onSkip, busy = false, nextTestid = 'btn-next' }) {
  return (
    <div className="pf-nav">
      {onBack ? <button type="button" className="pf-btn pf-btn-ghost" onClick={onBack} data-testid="btn-back">Précédent</button> : <span />}
      <span className="pf-nav-right">
        {onSkip && <button type="button" className="pf-link" onClick={onSkip} data-testid="btn-skip">Passer cette étape</button>}
        <button type="button" className="pf-btn pf-btn-primary" onClick={onNext} disabled={busy} data-testid={nextTestid}>
          {nextLabel}
        </button>
      </span>
    </div>
  );
}

/** Champ simple : libellé relié, aide, erreur sous le champ (aria-invalid, aria-describedby). */
export function Field({ id, label, hint, error, optional = false, suffix, children, render }) {
  const describedBy = [hint && `${id}-hint`, error && `${id}-err`].filter(Boolean).join(' ') || undefined;
  const control = render
    ? render({ id, 'aria-invalid': error ? 'true' : undefined, 'aria-describedby': describedBy, className: `pf-input${error ? ' is-err' : ''}` })
    : children;
  return (
    <div className="pf-field">
      <label className="pf-label" htmlFor={id}>
        {label}{optional && <span className="pf-optional"> (facultatif)</span>}
      </label>
      {suffix ? <div className={`pf-affix${error ? ' is-err' : ''}`}>{control}<span className="pf-suffix">{suffix}</span></div> : control}
      {hint && <p className="pf-hint" id={`${id}-hint`}>{hint}</p>}
      {error && <p className="pf-err" id={`${id}-err`} data-testid={`err-${id}`}>{error}</p>}
    </div>
  );
}

/**
 * Pastilles de choix. `multi` : plusieurs choix (role="checkbox", aria-checked), au plus `max` ; sinon un seul
 * (aria-pressed). `visible` : nombre de pastilles montrées avant « Voir les N … » (`moreLabel`).
 * `options` : [{ id, label, icon? }] ; `testid(o)` : identifiant de test de chaque pastille.
 */
export function ChoicePills({
  legend, hint, error, options, value, onChange, multi = false, max = Infinity, visible, moreLabel,
  testid, tiles = false, optional = false, errorId,
}) {
  const [open, setOpen] = useState(false);
  const chosen = (id) => (multi ? value.includes(id) : value === id);
  const shown = visible && !open ? options.filter((o, i) => i < visible || chosen(o.id)) : options;
  const full = multi && value.length >= max;
  return (
    <fieldset className="pf-fieldset" aria-describedby={error ? errorId : undefined}>
      <legend className="pf-label">
        {legend}{optional && <span className="pf-optional"> (facultatif)</span>}
        {multi && Number.isFinite(max) && <span className="pf-optional"> ({max} au plus)</span>}
      </legend>
      <div className={tiles ? 'pf-tiles' : 'pf-pills'}>
        {shown.map((o) => {
          const on = chosen(o.id);
          const blocked = !on && full;
          const Icon = o.icon;
          return (
            <button
              key={o.id}
              type="button"
              className={`${tiles ? 'pf-tile' : 'pf-pill'}${on ? ' is-on' : ''}${blocked ? ' is-blocked' : ''}`}
              {...(multi ? { role: 'checkbox', 'aria-checked': on } : { 'aria-pressed': on })}
              aria-disabled={blocked || undefined}
              onClick={() => { if (!blocked) onChange(o.id); }}
              data-testid={testid ? testid(o) : undefined}
            >
              {Icon && <Icon size={tiles ? 24 : 16} aria-hidden="true" />}
              {o.label}
            </button>
          );
        })}
        {visible && options.length > visible && (
          <button type="button" className="pf-link" aria-expanded={open} onClick={() => setOpen((x) => !x)}>
            {open ? 'Voir moins' : `Voir les ${options.length} ${moreLabel}`}
          </button>
        )}
      </div>
      {hint && <p className="pf-hint">{hint}</p>}
      {error && <p className="pf-err" id={errorId}>{error}</p>}
    </fieldset>
  );
}

/** Choix exclusif présenté en cartes (mode de prix). `options` : [{ id, title, text }]. */
export function ChoiceCards({ legend, name, options, value, onChange, testid }) {
  return (
    <fieldset className="pf-fieldset">
      <legend className="pf-label">{legend}</legend>
      <div className="pf-cards">
        {options.map((o) => (
          <label key={o.id} className={`pf-choice${value === o.id ? ' is-on' : ''}`} data-testid={testid ? testid(o) : undefined}>
            <input type="radio" name={name} value={o.id} checked={value === o.id} onChange={() => onChange(o.id)} />
            <span><strong>{o.title}</strong><span className="pf-choice-text">{o.text}</span></span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * Devises d'une annonce : 3 au plus, une principale (celle du prix). Le cedi (non payable par carte) se choisit mais
 * est signalé par la couleur ; sans devise payable, une note le dit (`sf-no-card-payable`).
 */
export function CurrencyPicker({ currencies, primary, onChange, error }) {
  const [adding, setAdding] = useState(false);
  const byCode = Object.fromEntries(CURRENCIES_LIST.map((c) => [c.code, c]));
  const others = CURRENCIES_LIST.filter((c) => !currencies.includes(c.code));
  const full = currencies.length >= MAX_CURRENCIES;
  const add = (code) => { onChange([...currencies, code], primary); setAdding(false); };
  const remove = (code) => onChange(currencies.filter((c) => c !== code), primary);
  const noCard = !currencies.some(ccyStripeOk);
  return (
    <fieldset className="pf-fieldset" aria-describedby={error ? 'pf-currencies-err' : undefined}>
      <legend className="pf-label">
        Dans quelles devises ?<span className="pf-optional"> ({MAX_CURRENCIES} au plus ; la principale est celle du prix)</span>
      </legend>
      <div className="pf-pills">
        {currencies.map((code) => {
          const c = byCode[code];
          const isPrimary = code === primary;
          return (
            <span key={code} className={`pf-ccy${isPrimary ? ' is-primary' : ''}${!ccyStripeOk(code) ? ' is-unpayable' : ''}`} data-testid={`ccy-${code}`}>
              {c?.symbol} {code}{isPrimary && <span className="pf-ccy-tag"> · principale</span>}
              {!isPrimary && (
                <button type="button" className="pf-ccy-act" onClick={() => onChange(currencies, code)} aria-label={`Définir ${code} comme principale`}>
                  Principale
                </button>
              )}
              {!isPrimary && (
                <button type="button" className="pf-ccy-x" onClick={() => remove(code)} aria-label={`Retirer ${code}`}>
                  <X size={14} aria-hidden="true" />
                </button>
              )}
            </span>
          );
        })}
        {!full && (
          <button type="button" className="pf-pill pf-pill-add" aria-expanded={adding} onClick={() => setAdding((x) => !x)} data-testid="currency-add">
            <Plus size={16} aria-hidden="true" /> Ajouter une devise
          </button>
        )}
      </div>
      {adding && !full && (
        <div className="pf-pills pf-ccy-list" role="group" aria-label="Devises à ajouter">
          {others.map((c) => (
            <button key={c.code} type="button" className={`pf-pill${c.stripe ? '' : ' is-unpayable'}`} onClick={() => add(c.code)}>
              {c.symbol} {c.code} · {c.name}{!c.stripe && ' (non payable par carte)'}
            </button>
          ))}
        </div>
      )}
      {noCard && currencies.length > 0 && (
        <p className="pf-note pf-note-warn" data-testid="sf-no-card-payable">
          Le cedi ne peut pas être payé par carte : il sert à afficher votre prix. Ajoutez une devise payable (par exemple
          l'euro ou le franc CFA) pour que l'on puisse vous faire une proposition.
        </p>
      )}
      {error && !noCard && <p className="pf-err" id="pf-currencies-err">{error}</p>}
    </fieldset>
  );
}

/** Bouton « Rédiger pour moi » (aide de Claude, `ai_assist.py`). */
export function DraftButton({ onClick, busy }) {
  return (
    <button type="button" className="pf-btn-ai" onClick={onClick} disabled={busy}>
      <Sparkles size={15} aria-hidden="true" /> {busy ? 'Rédaction…' : 'Rédiger pour moi'}
    </button>
  );
}

/** Ligne du récapitulatif : nom de l'étape et lien « Modifier ». */
export function RecapLinks({ steps, onGo }) {
  return (
    <dl className="pf-recap-links">
      {steps.filter((s) => s.key !== 'recap').map((s) => (
        <div key={s.key}>
          <dt>{s.label}</dt>
          <dd><button type="button" className="pf-link" onClick={() => onGo(s.key)} data-testid={`recap-edit-${s.key}`}>Modifier</button></dd>
        </div>
      ))}
    </dl>
  );
}

